// SCR-STAFF-414 — گزارش صندوق و فروش (US-BIL-150): بازه تاریخ (کسب‌وکاری شعبه)، فروش ناخالص/تخفیف/سهم بیمه/مالیات/خالص، دریافت به تفکیک روش،
// صندوق، صندوقدار، خدمت و ارائه‌دهنده، شیفت‌ها و مغایرت‌ها، ابطال/اعلامیه/استرداد، بیعانه‌ها و پرداخت‌های صندوق؛ خروجی CSV.
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { unwrap, type BilReportGroupTotal } from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, faDigits, JalaliDatePickerRHF, KeyValueList, ListPage, LoadingState, toBusinessDate } from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Amount, bilKeys, num, saveBlob, SCR_STAFF_414, SessionStatusChip } from './common';


export function CashReportPage() {
  const { t } = useTranslation('bil');
  const { tenantApi, session } = useTenantSession();
  // تاریخ کسب‌وکاری yyyy-MM-dd برای API؛ ورود و نمایش شمسی (ADR-014).
  const { control, watch } = useForm<{ from: string | null; to: string | null }>({ defaultValues: { from: toBusinessDate(new Date()), to: toBusinessDate(new Date()) } });
  const from = watch('from') ?? toBusinessDate(new Date());
  const to = watch('to') ?? from;
  const report = useQuery({
    queryKey: bilKeys.report(session?.tenantId, `${from}|${to}`),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/reports/cash', { params: { query: { from, to } } })),
  });
  const exportCsv = async () => {
    const response = await tenantApi.GET('/api/v1/billing/reports/cash/export', { params: { query: { from, to } }, parseAs: 'blob' });
    if (response.response.ok && response.data) {
      saveBlob(response.data as unknown as Blob, `cash-report-${from}-${to}.csv`);
    }
  };
  const data = report.data;
  return (
    <ListPage
      screenId={SCR_STAFF_414}
      titleKey="bil:report.title"
      subtitleKey="bil:report.subtitle"
      filters={
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <JalaliDatePickerRHF control={control} name="from" labelKey="bil:report.from" />
          <JalaliDatePickerRHF control={control} name="to" labelKey="bil:report.to" />
          <Button variant="outlined" onClick={() => void exportCsv()} data-testid="bil-report-export">
            {t('bil:report.export')}
          </Button>
        </Stack>
      }
    >
      {report.isPending ? <LoadingState rows={4} /> : null}
      {report.error ? <ErrorState error={report.error} onRetry={() => void report.refetch()} /> : null}
      {data ? (
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              {t('bil:report.sales')}
            </Typography>
            <KeyValueList
              columns={3}
              items={[
                { labelKey: 'bil:report.invoice_count', value: faDigits(num(data.sales.invoiceCount)) },
                { labelKey: 'bil:report.gross', value: <Amount money={data.sales.gross} /> },
                { labelKey: 'bil:report.discount', value: <Amount money={data.sales.discount} /> },
                { labelKey: 'bil:report.insurance', value: <Amount money={data.sales.insuranceShare} /> },
                { labelKey: 'bil:report.tax', value: <Amount money={data.sales.tax} /> },
                { labelKey: 'bil:report.net', value: <Amount money={data.sales.net} testId="bil-report-net" /> },
              ]}
            />
          </Paper>
          <Group title={t('bil:report.by_method')} rows={data.receiptsByMethod} label={(row) => t(`bil:method.${row.key}`, { defaultValue: row.key })} testId="bil-report-method" />
          <Group title={t('bil:report.by_register')} rows={data.byRegister} />
          <Group title={t('bil:report.by_cashier')} rows={data.byCashier} />
          <Group title={t('bil:report.by_service')} rows={data.byService} />
          <Group title={t('bil:report.by_provider')} rows={data.byProvider} />
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              {t('bil:report.sessions')}
            </Typography>
            <Table size="small" aria-label={t('bil:report.sessions')}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('bil:session.register')}</TableCell>
                  <TableCell>{t('bil:session.cashier')}</TableCell>
                  <TableCell>{t('bil:session.status')}</TableCell>
                  <TableCell>{t('bil:session.difference')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.sessions.map((row) => (
                  <TableRow key={row.sessionId} data-testid="bil-report-session">
                    <TableCell>{row.registerName ?? '—'}</TableCell>
                    <TableCell>{row.cashierName ?? '—'}</TableCell>
                    <TableCell>
                      <SessionStatusChip status={row.status} />
                    </TableCell>
                    <TableCell>
                      <Amount money={row.totalDiscrepancy} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              {t('bil:report.corrections')}
            </Typography>
            <KeyValueList
              columns={3}
              items={[
                { labelKey: 'bil:report.voids', value: <span data-testid="bil-report-voids">{faDigits(num(data.corrections.voidCount))} · <Amount money={data.corrections.voidAmount} /></span> },
                { labelKey: 'bil:report.credit_notes', value: <span>{faDigits(num(data.corrections.creditNoteCount))} · <Amount money={data.corrections.creditNoteAmount} /></span> },
                { labelKey: 'bil:report.refunds', value: <span>{faDigits(num(data.corrections.refundCount))} · <Amount money={data.corrections.refundAmount} /></span> },
                { labelKey: 'bil:report.deposits_received', value: <Amount money={data.deposits.received} /> },
                { labelKey: 'bil:report.deposits_forfeited', value: <Amount money={data.deposits.forfeited} /> },
                { labelKey: 'bil:report.deposits_refunded', value: <Amount money={data.deposits.refunded} /> },
                { labelKey: 'bil:report.petty_cash', value: <Amount money={data.pettyCash} /> },
                { labelKey: 'bil:report.package_sales', value: <Amount money={data.packageSales.amount} /> },
              ]}
            />
          </Paper>
        </Stack>
      ) : null}
    </ListPage>
  );
}

function Group({ title, rows, label, testId }: { title: string; rows: BilReportGroupTotal[]; label?: (row: BilReportGroupTotal) => string; testId?: string }) {
  const { t } = useTranslation('bil');
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        {title}
      </Typography>
      {rows.length === 0 ? (
        <Typography color="text.secondary">{t('bil:finance.none')}</Typography>
      ) : (
        <Table size="small" aria-label={title}>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.key} data-testid={testId} data-key={row.key}>
                <TableCell>{label ? label(row) : (row.label ?? row.key)}</TableCell>
                <TableCell>{faDigits(num(row.count))}</TableCell>
                <TableCell>
                  <Amount money={row.amount} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}
